/**
 * Reusable GSAP animation hooks. Every hook here:
 *   • registers nothing itself (plugins come pre-registered from `lib/gsap`),
 *   • scopes its work to a component ref via `gsap.context(...)`,
 *   • reverts on cleanup (StrictMode double-mount safe, no leaks),
 *   • checks `prefersReducedMotion()` and jumps to the final state instead of
 *     animating when the user asks for less motion.
 *
 * Attach the returned ref to the element you want animated.
 */
import { useLayoutEffect, useRef } from 'react';
import { gsap, prefersReducedMotion } from '../../lib/gsap';
import { DUR, EASE, STAGGER } from './config';

/** Fade + lift an element into view once when it scrolls near the viewport. */
export function useGsapReveal<T extends HTMLElement = HTMLDivElement>(opts?: {
  y?: number;
  x?: number;
  delay?: number;
  duration?: number;
  start?: string;
}) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) {
      gsap.set(el, { opacity: 1, x: 0, y: 0 });
      return;
    }
    const ctx = gsap.context(() => {
      gsap.from(el, {
        opacity: 0,
        y: opts?.y ?? 24,
        x: opts?.x ?? 0,
        duration: opts?.duration ?? DUR.reveal,
        delay: opts?.delay ?? 0,
        ease: EASE.entrance,
        scrollTrigger: { trigger: el, start: opts?.start ?? 'top 85%', once: true },
      });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

/** Stagger a container's children (or a selector within it) into view once. */
export function useGsapStagger<T extends HTMLElement = HTMLDivElement>(opts?: {
  selector?: string;
  y?: number;
  stagger?: number;
  duration?: number;
  start?: string;
}) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const targets: ArrayLike<Element> = opts?.selector
      ? el.querySelectorAll(opts.selector)
      : el.children;
    if (!targets.length) return;
    if (prefersReducedMotion()) {
      gsap.set(targets, { opacity: 1, y: 0 });
      return;
    }
    const ctx = gsap.context(() => {
      gsap.from(targets, {
        opacity: 0,
        y: opts?.y ?? 16,
        duration: opts?.duration ?? DUR.reveal,
        ease: EASE.entrance,
        stagger: opts?.stagger ?? STAGGER.base,
        scrollTrigger: { trigger: el, start: opts?.start ?? 'top 85%', once: true },
      });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

/**
 * Count a number up from 0 → `value` when it scrolls into view. Writes into a
 * `<span>` via the returned ref (no per-frame React re-render). `format` controls
 * the displayed string (e.g. a currency/locale formatter). Reduced motion snaps.
 */
export function useGsapCounter(
  value: number,
  opts?: { duration?: number; format?: (n: number) => string; start?: string },
) {
  const ref = useRef<HTMLSpanElement>(null);
  const format = opts?.format ?? ((n: number) => String(Math.round(n)));
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion() || !Number.isFinite(value)) {
      el.textContent = format(value);
      return;
    }
    el.textContent = format(0);
    const obj = { v: 0 };
    const ctx = gsap.context(() => {
      gsap.to(obj, {
        v: value,
        duration: opts?.duration ?? 0.9,
        ease: EASE.standard,
        onUpdate: () => {
          if (ref.current) ref.current.textContent = format(obj.v);
        },
        scrollTrigger: { trigger: el, start: opts?.start ?? 'top 90%', once: true },
      });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return ref;
}

/**
 * Scroll-linked parallax drift. As the element passes through the viewport it
 * translates from +`clamp` to −`clamp` px on `axis`. `speed` sign flips direction.
 * GPU transform only; **disabled entirely under reduced motion**. Public-site use.
 */
export function useGsapParallax<T extends HTMLElement = HTMLDivElement>(opts?: {
  speed?: number;
  axis?: 'y' | 'x';
  clamp?: number;
}) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    const speed = opts?.speed ?? 0.2;
    const axis = opts?.axis ?? 'y';
    const d = (opts?.clamp ?? 28) * (speed < 0 ? -1 : 1);
    const from = axis === 'y' ? { y: d } : { x: d };
    const to = axis === 'y' ? { y: -d } : { x: -d };
    const ctx = gsap.context(() => {
      gsap.fromTo(el, from, {
        ...to,
        ease: 'none',
        scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true },
      });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

/**
 * Stroke-draw ("grow-in") for inline SVG line-art — the paths draw themselves on
 * scroll-in. Free implementation (measures path length, animates strokeDashoffset;
 * no paid DrawSVGPlugin). Attach the ref to the `<svg>`. Reduced motion → shown solid.
 */
export function useGsapDraw<T extends Element = SVGSVGElement>(opts?: {
  duration?: number;
  stagger?: number;
  start?: string;
  selector?: string;
}) {
  // Ref can attach to the <svg> itself OR a wrapping element — querySelectorAll
  // finds descendant geometry either way, so illustrations need no changes.
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const root = ref.current;
    if (!root) return;
    const paths = root.querySelectorAll<SVGGeometryElement>(
      opts?.selector ?? 'path, line, polyline, polygon',
    );
    if (!paths.length) return;
    if (prefersReducedMotion()) return; // leave fully drawn
    const ctx = gsap.context(() => {
      paths.forEach((p) => {
        const len = typeof p.getTotalLength === 'function' ? p.getTotalLength() : 0;
        if (!len) return;
        gsap.set(p, { strokeDasharray: len, strokeDashoffset: len });
      });
      gsap.to(paths, {
        strokeDashoffset: 0,
        duration: opts?.duration ?? 1.1,
        ease: EASE.standard,
        stagger: opts?.stagger ?? 0.08,
        scrollTrigger: { trigger: root, start: opts?.start ?? 'top 80%', once: true },
      });
    }, root);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ref;
}

/**
 * Play a subtle entrance whenever `deps` change (e.g. the route path) — used by
 * the shell to give a light page transition under the persistent header.
 */
export function useGsapPageEnter<T extends HTMLElement = HTMLDivElement>(deps: unknown[]) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      gsap.from(el, { opacity: 0, y: 8, duration: DUR.slow, ease: EASE.entrance });
    }, el);
    return () => ctx.revert();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return ref;
}
